import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import AuthContext from '../../context/AuthContext';
import AnalyticsService from '../../services/analyticsService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProgressCharts = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('monthly');
  const [exerciseId, setExerciseId] = useState('');
  const [exercises, setExercises] = useState([]);
  const [volumeData, setVolumeData] = useState({
    labels: [],
    datasets: []
  });
  const [exerciseProgressData, setExerciseProgressData] = useState({
    labels: [],
    datasets: []
  });
  const [workoutFrequencyData, setWorkoutFrequencyData] = useState({
    labels: [],
    datasets: []
  });

  useEffect(() => {
    fetchExercises();
    fetchVolumeData();
    fetchWorkoutFrequency();
  }, [token, timeframe]);

  useEffect(() => {
    if (exerciseId) {
      fetchExerciseProgress();
    }
  }, [token, exerciseId, timeframe]);

  const fetchExercises = async () => {
    try {
      const data = await AnalyticsService.getUserExercises(token);
      
      if (data.success) {
        setExercises(data.exercises || []);
        
        // Set default exercise if available
        if (data.exercises && data.exercises.length > 0 && !exerciseId) {
          setExerciseId(data.exercises[0]._id);
        }
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const fetchVolumeData = async () => {
    try {
      const data = await AnalyticsService.getVolumeAnalytics(timeframe, token);
      
      if (data.success) {
        setVolumeData({
          labels: data.labels || [],
          datasets: [
            {
              label: 'Total Volume (kg)',
              data: data.values || [],
              fill: false,
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              borderColor: 'rgba(75, 192, 192, 1)',
              tension: 0.1
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching volume data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExerciseProgress = async () => {
    try {
      const data = await AnalyticsService.getExerciseProgress(exerciseId, timeframe, token);
      
      if (data.success) {
        setExerciseProgressData({
          labels: data.labels || [],
          datasets: [
            {
              label: 'Max Weight (kg)',
              data: data.maxWeights || [],
              fill: false,
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              borderColor: 'rgba(54, 162, 235, 1)',
              tension: 0.1
            },
            {
              label: 'Average Weight (kg)',
              data: data.avgWeights || [],
              fill: false,
              backgroundColor: 'rgba(255, 99, 132, 0.2)',
              borderColor: 'rgba(255, 99, 132, 1)',
              tension: 0.1
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching exercise progress:', error);
    }
  };

  const fetchWorkoutFrequency = async () => {
    try {
      const data = await AnalyticsService.getWorkoutFrequency(timeframe, token);
      
      if (data.success) {
        setWorkoutFrequencyData({
          labels: data.labels || [],
          datasets: [
            {
              label: 'Workout Frequency',
              data: data.values || [],
              backgroundColor: 'rgba(153, 102, 255, 0.6)',
              borderColor: 'rgba(153, 102, 255, 1)',
              borderWidth: 1
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching workout frequency:', error);
    }
  };

  const handleTimeframeChange = (e) => {
    setTimeframe(e.target.value);
  };

  const handleExerciseChange = (e) => {
    setExerciseId(e.target.value);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  if (loading && (!volumeData.labels.length || !workoutFrequencyData.labels.length)) {
    return <div className="loading">Loading progress data...</div>;
  }

  return (
    <div className="progress-charts">
      <h1>Your Progress</h1>
      
      <div className="chart-controls">
        <div className="form-group">
          <label htmlFor="timeframe">Timeframe:</label>
          <select
            id="timeframe"
            value={timeframe}
            onChange={handleTimeframeChange}
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>
      
      {/* Volume Chart */}
      <div className="chart-container">
        <h2>Total Volume Progress</h2>
        <div className="chart-wrapper">
          {volumeData.labels.length > 0 ? (
            <Line data={volumeData} options={chartOptions} height={300} />
          ) : (
            <p className="no-data">No volume data available for the selected timeframe.</p>
          )}
        </div>
      </div>
      
      {/* Workout Frequency Chart */}
      <div className="chart-container">
        <h2>Workout Frequency</h2>
        <div className="chart-wrapper">
          {workoutFrequencyData.labels.length > 0 ? (
            <Bar data={workoutFrequencyData} options={chartOptions} height={300} />
          ) : (
            <p className="no-data">No workout frequency data available for the selected timeframe.</p>
          )}
        </div>
      </div>
      
      {/* Exercise Progress Chart */}
      <div className="chart-container">
        <h2>Exercise Progress</h2>
        <div className="form-group">
          <label htmlFor="exercise">Select Exercise:</label>
          <select
            id="exercise"
            value={exerciseId}
            onChange={handleExerciseChange}
          >
            {exercises.map(exercise => (
              <option key={exercise._id} value={exercise._id}>
                {exercise.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="chart-wrapper">
          {exerciseProgressData.labels.length > 0 ? (
            <Line data={exerciseProgressData} options={chartOptions} height={300} />
          ) : (
            <p className="no-data">No progress data available for the selected exercise.</p>
          )}
        </div>
      </div>
      
      <div className="chart-info">
        <h3>Understanding Your Progress</h3>
        <ul>
          <li><strong>Total Volume:</strong> Sum of (weight × reps) for all exercises.</li>
          <li><strong>Workout Frequency:</strong> Number of workouts completed in each period.</li>
          <li><strong>Exercise Progress:</strong> Track your weight progression for specific exercises.</li>
        </ul>
      </div>
    </div>
  );
};

export default ProgressCharts;
